/**
 * Resolve Nostr bech32 / hex refs and fetch content from relays.
 * Used for context packs, @mentions, and the automatic pre-send Nostr fetch pass.
 */

import { nip19 } from 'nostr-tools';
import type { NostrEvent } from '@nostrify/types';

import { setProfileCached } from '@/lib/profileCache';
import { rawRelayQuery } from '@/lib/rawRelayQuery';

/** Minimal filter shape accepted by Nostrify query. */
export type NostrFilter = {
  ids?: string[];
  authors?: string[];
  kinds?: number[];
  since?: number;
  until?: number;
  limit?: number;
  /** NIP-50 full-text search (relay support varies). */
  search?: string;
  '#d'?: string[];
  '#e'?: string[];
  [key: `#${string}`]: string[] | undefined;
};

export type NostrQueryFn = (
  filters: NostrFilter[],
  opts?: { signal?: AbortSignal },
) => Promise<NostrEvent[]>;

export type ResolvedNostrRef =
  | { kind: 'note'; id: string; relays?: string[] }
  | { kind: 'nevent'; id: string; author?: string; relays?: string[]; eventKind?: number }
  | { kind: 'npub'; pubkey: string }
  | { kind: 'nprofile'; pubkey: string; relays?: string[] }
  /**
   * Addressable event (NIP-19 naddr).
   * IMPORTANT: use `eventKind` for the Nostr event kind — never reuse the
   * discriminant field `kind` (that caused naddr → "Could not resolve event id").
   */
  | {
      kind: 'naddr';
      identifier: string;
      pubkey: string;
      eventKind: number;
      relays?: string[];
    }
  | { kind: 'hex-event'; id: string }
  | { kind: 'hex-pubkey'; pubkey: string };

/** Single-token bech32 (no whitespace). */
const BECH32_RE =
  /\b(note1[02-9ac-hj-np-z]{6,}|nevent1[02-9ac-hj-np-z]{6,}|npub1[02-9ac-hj-np-z]{6,}|nprofile1[02-9ac-hj-np-z]{6,}|naddr1[02-9ac-hj-np-z]{6,})\b/gi;

/**
 * Bech32 that may be soft-wrapped / split across whitespace or newlines
 * (common when pasting long naddr/nevent from clients).
 */
const BECH32_WRAPPED_RE =
  /\b((?:note|nevent|npub|nprofile|naddr)1[02-9ac-hj-np-z][02-9ac-hj-np-z\s]{20,})\b/gi;

/** Strip whitespace inside a possible wrapped bech32 and validate. */
function coalesceBech32(raw: string): string | null {
  const compact = raw.replace(/\s+/g, '');
  if (
    !/^(note|nevent|npub|nprofile|naddr)1[02-9ac-hj-np-z]{6,}$/i.test(compact)
  ) {
    return null;
  }
  // Must decode to be real
  try {
    nip19.decode(compact);
    return compact;
  } catch {
    return null;
  }
}

export function extractNostrRefs(text: string): string[] {
  if (!text?.trim()) return [];
  const found = new Set<string>();

  // Prefer coalesced wrapped forms first (covers multiline pastes)
  for (const m of text.matchAll(BECH32_WRAPPED_RE)) {
    const c = coalesceBech32(m[1]!);
    if (c) found.add(c);
  }
  for (const m of text.matchAll(BECH32_RE)) {
    found.add(m[1]!);
  }
  // Only treat bare hex as event ids when prefixed with nostr: or #e
  for (const m of text.matchAll(/(?:nostr:|note:|#e\s*)([0-9a-f]{64})\b/gi)) {
    found.add(m[1]!.toLowerCase());
  }
  return [...found];
}

export function parseNostrRef(raw: string): ResolvedNostrRef | null {
  // Coalesce whitespace so line-wrapped naddr/nevent still parse
  let s = raw.trim().replace(/^nostr:/i, '');
  s = s.replace(/\s+/g, '');
  if (!s) return null;

  if (/^[0-9a-f]{64}$/i.test(s)) {
    return { kind: 'hex-event', id: s.toLowerCase() };
  }

  try {
    const decoded = nip19.decode(s);
    switch (decoded.type) {
      case 'note':
        return { kind: 'note', id: decoded.data as string };
      case 'nevent': {
        const d = decoded.data as {
          id: string;
          relays?: string[];
          author?: string;
          kind?: number;
        };
        return {
          kind: 'nevent',
          id: d.id,
          author: d.author,
          relays: d.relays,
          eventKind: d.kind,
        };
      }
      case 'npub':
        return { kind: 'npub', pubkey: decoded.data as string };
      case 'nprofile': {
        const d = decoded.data as { pubkey: string; relays?: string[] };
        return { kind: 'nprofile', pubkey: d.pubkey, relays: d.relays };
      }
      case 'naddr': {
        const d = decoded.data as {
          identifier: string;
          pubkey: string;
          kind: number;
          relays?: string[];
        };
        return {
          kind: 'naddr',
          identifier: d.identifier,
          pubkey: d.pubkey,
          // Must NOT be named `kind` — that overwrites the discriminant
          eventKind: d.kind,
          relays: d.relays,
        };
      }
      default:
        return null;
    }
  } catch {
    return null;
  }
}

function short(hex: string, n = 8): string {
  if (hex.length <= n * 2) return hex;
  return `${hex.slice(0, n)}…${hex.slice(-n)}`;
}

function kindLabel(kind: number): string {
  switch (kind) {
    case 0:
      return 'profile';
    case 1:
      return 'note';
    case 3:
      return 'follow list';
    case 6:
      return 'repost';
    case 7:
      return 'reaction';
    case 30023:
      return 'article';
    case 31990:
      return 'app handler';
    case 10002:
      return 'relay list';
    default:
      return `kind ${kind}`;
  }
}

function truncateContent(content: string, max: number): string {
  if (content.length <= max) return content;
  return content.slice(0, max) + '\n… [truncated]';
}

/** Rich event formatter for packs and auto-fetch. */
export function formatEventRich(
  ev: NostrEvent,
  label?: string,
  opts?: { maxContent?: number },
): string {
  const maxContent = opts?.maxContent ?? 12_000;
  const head = label || kindLabel(ev.kind);
  const when = new Date(ev.created_at * 1000).toISOString();

  // Long-form articles (NIP-23)
  if (ev.kind === 30023) {
    const title = ev.tags.find((t) => t[0] === 'title')?.[1] || '';
    const summary = ev.tags.find((t) => t[0] === 'summary')?.[1] || '';
    const d = ev.tags.find((t) => t[0] === 'd')?.[1] || '';
    const image =
      ev.tags.find((t) => t[0] === 'image')?.[1] ||
      ev.tags.find((t) => t[0] === 'thumb')?.[1] ||
      '';
    const body = truncateContent(ev.content || '', maxContent);
    return (
      `### Article${title ? `: ${title}` : ''}\n` +
      `id: ${ev.id}\nauthor: ${ev.pubkey}\ncreated_at: ${when}` +
      (d ? `\nd: ${d}` : '') +
      (image ? `\nimage: ${image}` : '') +
      (summary ? `\nsummary: ${summary}` : '') +
      `\n\n${body}`
    );
  }

  // App handler (NIP-89)
  if (ev.kind === 31990) {
    let meta: Record<string, unknown> = {};
    try {
      meta = JSON.parse(ev.content || '{}') as Record<string, unknown>;
    } catch {
      /* ignore */
    }
    const name = typeof meta.name === 'string' ? meta.name : '';
    const about = typeof meta.about === 'string' ? meta.about : '';
    const website = typeof meta.website === 'string' ? meta.website : '';
    return (
      `### App${name ? `: ${name}` : ''}\n` +
      `id: ${ev.id}\npubkey: ${ev.pubkey}\ncreated_at: ${when}` +
      (website ? `\nwebsite: ${website}` : '') +
      (about ? `\n\n${about}` : '')
    );
  }

  // Reactions — compact
  if (ev.kind === 7) {
    const eTag = ev.tags.find((t) => t[0] === 'e')?.[1];
    return (
      `### Reaction\n` +
      `author: ${short(ev.pubkey)} · ${when}\n` +
      `content: ${ev.content || '+'}` +
      (eTag ? `\ntarget: ${short(eTag)}` : '')
    );
  }

  const usefulTags = ev.tags
    .filter((t) => t[0] && !['nonce', 'client'].includes(t[0]))
    .slice(0, 16)
    .map((t) => t.join(':'))
    .join(', ');

  const content = truncateContent(ev.content || '', maxContent);
  return (
    `### Nostr ${head}\n` +
    `id: ${ev.id}\npubkey: ${ev.pubkey}\nkind: ${ev.kind} (${kindLabel(ev.kind)})\n` +
    `created_at: ${when}` +
    (usefulTags ? `\ntags: ${usefulTags}` : '') +
    `\n\n${content}`
  );
}

/** @deprecated Prefer formatEventRich */
function formatEvent(ev: NostrEvent, label?: string): string {
  return formatEventRich(ev, label);
}

export function formatProfileRich(
  ev: NostrEvent | undefined,
  pubkey: string,
  label?: string,
): string {
  const head = label || 'Nostr profile';
  if (!ev) {
    return `### ${head}\npubkey: ${pubkey}\n(no kind-0 metadata found on connected relays)`;
  }

  // Cache for typeahead
  try {
    let meta: Record<string, unknown> = {};
    try {
      meta = JSON.parse(ev.content) as Record<string, unknown>;
    } catch {
      return formatEventRich(ev, 'profile (kind 0)');
    }
    void setProfileCached(ev, meta as never);

    const name = typeof meta.name === 'string' ? meta.name : '';
    const display = typeof meta.display_name === 'string' ? meta.display_name : '';
    const about = typeof meta.about === 'string' ? meta.about : '';
    const nip05 = typeof meta.nip05 === 'string' ? meta.nip05 : '';
    const lud16 = typeof meta.lud16 === 'string' ? meta.lud16 : '';
    const website = typeof meta.website === 'string' ? meta.website : '';
    const picture = typeof meta.picture === 'string' ? meta.picture : '';
    const banner =
      (typeof meta.banner === 'string' && meta.banner) ||
      (typeof meta.image === 'string' && meta.image) ||
      '';

    let npub = '';
    try {
      npub = nip19.npubEncode(pubkey);
    } catch {
      npub = pubkey;
    }

    const lines = [
      `### ${head}`,
      `npub: ${npub}`,
      `pubkey: ${pubkey}`,
      display || name ? `name: ${display || name}` : null,
      name && display && name !== display ? `handle: ${name}` : null,
      nip05 ? `nip05: ${nip05}` : null,
      lud16 ? `lightning: ${lud16}` : null,
      website ? `website: ${website}` : null,
      picture ? `picture: ${picture}` : null,
      banner ? `banner: ${banner}` : null,
      about ? `\n${about}` : null,
    ].filter(Boolean);
    return lines.join('\n');
  } catch {
    return formatEventRich(ev, 'profile (kind 0)');
  }
}

/** @deprecated Prefer formatProfileRich */
function formatProfile(ev: NostrEvent | undefined, pubkey: string): string {
  return formatProfileRich(ev, pubkey);
}

/** Format a list of kind-1 notes for packs. */
export function formatNotesList(notes: NostrEvent[], label: string): string {
  if (!notes.length) {
    return `### ${label}\n(no recent public notes found on connected relays)`;
  }
  const blocks = notes.map((ev, i) => {
    const when = new Date(ev.created_at * 1000).toISOString();
    const content = truncateContent(ev.content || '', 1_500);
    return (
      `#### Note ${i + 1} · ${when}\n` +
      `id: ${ev.id}\n\n${content}`
    );
  });
  return `### ${label} (${notes.length})\n\n${blocks.join('\n\n')}`;
}

async function queryOne(
  query: NostrQueryFn,
  filters: NostrFilter[],
  signal?: AbortSignal,
  relayHints?: string[],
): Promise<NostrEvent | undefined> {
  const events = await queryWithHintFallback(query, filters, signal, relayHints);
  if (!events?.length) return undefined;
  return [...events].sort((a, b) => b.created_at - a.created_at)[0];
}

/**
 * Query the connected pool first; when nothing comes back and the ref
 * carried relay hints (nevent/naddr/nprofile), retry against those relays
 * directly. This is what makes long-tail articles/notes resolve even when
 * they live outside the user's read pool.
 */
export async function queryWithHintFallback(
  query: NostrQueryFn,
  filters: NostrFilter[],
  signal?: AbortSignal,
  relayHints?: string[],
): Promise<NostrEvent[]> {
  const events = await query(filters, { signal });
  if (events?.length) return events;
  const hints = (relayHints ?? []).filter((u) => /^wss?:\/\//i.test(u));
  if (!hints.length || signal?.aborted) return events ?? [];
  try {
    return await rawRelayQuery(hints, filters as never, {
      signal,
      timeoutMs: 6_000,
    });
  } catch {
    return events ?? [];
  }
}

/**
 * Fetch kind-0 for a pubkey and return formatted text (or null if missing).
 */
async function fetchAuthorProfileText(
  query: NostrQueryFn,
  pubkey: string,
  signal?: AbortSignal,
  label?: string,
  relayHints?: string[],
): Promise<string | null> {
  try {
    const ev = await queryOne(
      query,
      [{ kinds: [0], authors: [pubkey], limit: 1 }],
      signal,
      relayHints,
    );
    return formatProfileRich(ev, pubkey, label || 'Author profile');
  } catch {
    return null;
  }
}

/**
 * Append author profile under an event/article body when we know the pubkey.
 */
async function withAuthorProfile(
  body: string,
  pubkey: string | undefined,
  query: NostrQueryFn,
  signal?: AbortSignal,
  opts?: { includeAuthor?: boolean; relayHints?: string[] },
): Promise<string> {
  if (!pubkey || opts?.includeAuthor === false) return body;
  const profile = await fetchAuthorProfileText(
    query,
    pubkey,
    signal,
    'Author profile',
    opts?.relayHints,
  );
  if (!profile) {
    return (
      body +
      `\n\n### Author profile\npubkey: ${pubkey}\n(no kind-0 metadata found on connected relays)`
    );
  }
  return `${body}\n\n${profile}`;
}

/** Metadata extracted for UI chips (avatars, article banners). */
export interface FetchedEntityMeta {
  title?: string;
  image?: string;
  picture?: string;
  nip05?: string;
  displayName?: string;
  eventKind?: number;
  identifier?: string;
}

function metaFromEvent(ev: NostrEvent): FetchedEntityMeta {
  const meta: FetchedEntityMeta = {
    eventKind: ev.kind,
    identifier: ev.tags.find((t) => t[0] === 'd')?.[1],
  };
  if (ev.kind === 30023) {
    meta.title = ev.tags.find((t) => t[0] === 'title')?.[1];
    meta.image =
      ev.tags.find((t) => t[0] === 'image')?.[1] ||
      ev.tags.find((t) => t[0] === 'thumb')?.[1];
  }
  if (ev.kind === 0) {
    try {
      const m = JSON.parse(ev.content || '{}') as Record<string, unknown>;
      meta.picture = typeof m.picture === 'string' ? m.picture : undefined;
      // NIP-01 often uses banner for header image
      meta.image =
        (typeof m.banner === 'string' && m.banner) ||
        (typeof m.image === 'string' && m.image) ||
        undefined;
      meta.displayName =
        (typeof m.display_name === 'string' && m.display_name) ||
        (typeof m.name === 'string' && m.name) ||
        undefined;
      meta.nip05 = typeof m.nip05 === 'string' ? m.nip05 : undefined;
    } catch {
      /* ignore */
    }
  }
  return meta;
}

function metaFromProfileText(text: string): Partial<FetchedEntityMeta> {
  const picture = text.match(/^picture: (.+)$/m)?.[1]?.trim();
  const nip05 = text.match(/^nip05: (.+)$/m)?.[1]?.trim();
  const name = text.match(/^name: (.+)$/m)?.[1]?.trim();
  const image = text.match(/^image: (.+)$/m)?.[1]?.trim();
  return { picture, nip05, displayName: name, image };
}

export async function fetchNostrEntity(
  raw: string,
  query: NostrQueryFn,
  options?: {
    signal?: AbortSignal;
    timeoutMs?: number;
    /** When true (default for events), also fetch author kind-0 */
    includeAuthor?: boolean;
  },
): Promise<
  | {
      ok: true;
      text: string;
      ref: string;
      eventId?: string;
      pubkey?: string;
      meta?: FetchedEntityMeta;
    }
  | { ok: false; error: string; ref: string }
> {
  const ref = raw.trim().replace(/\s+/g, '');
  const parsed = parseNostrRef(ref);
  if (!parsed) {
    return { ok: false, error: 'Unrecognized Nostr reference', ref: raw.trim() };
  }

  // Articles / addressable events often need a bit longer on slow relays
  const defaultTimeout =
    parsed.kind === 'naddr' ? 14_000 : 10_000;
  const timeoutMs = options?.timeoutMs ?? defaultTimeout;
  const includeAuthor = options?.includeAuthor !== false;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const onOuterAbort = () => controller.abort();
  options?.signal?.addEventListener('abort', onOuterAbort);

  try {
    const signal = controller.signal;

    if (parsed.kind === 'npub' || parsed.kind === 'nprofile' || parsed.kind === 'hex-pubkey') {
      const pubkey =
        parsed.kind === 'hex-pubkey'
          ? parsed.pubkey
          : parsed.kind === 'npub'
            ? parsed.pubkey
            : parsed.pubkey;
      const hints = parsed.kind === 'nprofile' ? parsed.relays : undefined;
      const ev = await queryOne(
        query,
        [{ kinds: [0], authors: [pubkey], limit: 1 }],
        signal,
        hints,
      );
      const text = formatProfileRich(ev, pubkey);
      return {
        ok: true,
        text,
        ref,
        pubkey,
        meta: ev ? metaFromEvent(ev) : { ...metaFromProfileText(text) },
      };
    }

    if (parsed.kind === 'naddr') {
      const eventKind = parsed.eventKind;
      const filter: NostrFilter = {
        kinds: [eventKind],
        authors: [parsed.pubkey],
        '#d': [parsed.identifier],
        limit: 1,
      };
      let ev = await queryOne(query, [filter], signal, parsed.relays);

      // Fallback: some relays are flaky with #d — try without d, match in client
      if (!ev && parsed.identifier) {
        const loose = await queryWithHintFallback(
          query,
          [
            {
              kinds: [eventKind],
              authors: [parsed.pubkey],
              limit: 30,
            },
          ],
          signal,
          parsed.relays,
        );
        ev = [...(loose ?? [])]
          .filter((e) => e.tags.some((t) => t[0] === 'd' && t[1] === parsed.identifier))
          .sort((a, b) => b.created_at - a.created_at)[0];
      }

      if (!ev) {
        return {
          ok: false,
          error: `No addressable event found for naddr (kind ${eventKind}:${short(parsed.pubkey)}:${parsed.identifier}). Try a relay that hosts this article, or paste via + → Nostr.`,
          ref,
        };
      }
      const body = formatEventRich(
        ev,
        eventKind === 30023
          ? `article d=${parsed.identifier}`
          : `naddr kind ${eventKind} d=${parsed.identifier}`,
      );
      const text = await withAuthorProfile(body, ev.pubkey || parsed.pubkey, query, signal, {
        includeAuthor,
        relayHints: parsed.relays,
      });
      const meta = metaFromEvent(ev);
      // Author picture from appended profile block
      const authorPic = metaFromProfileText(text).picture;
      if (authorPic && !meta.picture) meta.picture = authorPic;
      return {
        ok: true,
        text,
        ref,
        eventId: ev.id,
        pubkey: ev.pubkey,
        meta,
      };
    }

    // note / nevent / hex event id
    const id =
      parsed.kind === 'note' || parsed.kind === 'nevent' || parsed.kind === 'hex-event'
        ? parsed.id
        : '';
    if (!id) {
      return { ok: false, error: 'Could not resolve event id', ref };
    }

    const hints = parsed.kind === 'nevent' ? parsed.relays : undefined;

    // Prefer filter by id only first (author filter can miss if nevent author hint is wrong)
    let filter: NostrFilter = { ids: [id], limit: 1 };
    let ev = await queryOne(query, [filter], signal, hints);
    if (!ev && parsed.kind === 'nevent' && parsed.author) {
      filter = { ids: [id], authors: [parsed.author], limit: 1 };
      ev = await queryOne(query, [filter], signal, hints);
    }
    if (!ev) {
      return {
        ok: false,
        error: `Event ${short(id)} not found on connected relays`,
        ref,
      };
    }

    const authorPubkey = ev.pubkey || (parsed.kind === 'nevent' ? parsed.author : undefined);
    const body = formatEventRich(ev, `event ${short(id)}`);
    const text = await withAuthorProfile(body, authorPubkey, query, signal, {
      includeAuthor,
      relayHints: hints,
    });
    const meta = metaFromEvent(ev);
    const authorPic = metaFromProfileText(text).picture;
    if (authorPic) meta.picture = authorPic;
    return {
      ok: true,
      text,
      ref,
      eventId: ev.id,
      pubkey: ev.pubkey,
      meta,
    };
  } catch (err) {
    const msg =
      err instanceof Error
        ? err.name === 'AbortError'
          ? 'Timed out fetching from relays'
          : err.message
        : 'Fetch failed';
    return { ok: false, error: msg, ref };
  } finally {
    clearTimeout(timer);
    options?.signal?.removeEventListener('abort', onOuterAbort);
  }
}

/**
 * Normalize a ref string for dedupe (lowercase bech32 / hex).
 * Strips internal whitespace so line-wrapped pastes match compact form.
 */
export function normalizeRefKey(ref: string): string {
  return ref.trim().toLowerCase().replace(/^nostr:/i, '').replace(/\s+/g, '');
}

/**
 * Scan text + context item refs, fetch unique entities, return a system block.
 * Skips refs already covered by packs/mentions (consumedKeys).
 */
export async function buildNostrFetchBlock(
  sources: string[],
  query: NostrQueryFn,
  options?: {
    signal?: AbortSignal;
    maxRefs?: number;
    /** Normalized ref keys already injected by packs/mentions */
    skipRefs?: string[];
  },
): Promise<string | null> {
  const maxRefs = options?.maxRefs ?? 8;
  const skip = new Set((options?.skipRefs ?? []).map(normalizeRefKey));
  const refs: string[] = [];
  const seen = new Set<string>();

  for (const src of sources) {
    for (const r of extractNostrRefs(src)) {
      const key = normalizeRefKey(r);
      if (seen.has(key) || skip.has(key)) continue;
      // Also skip if any skip key is contained (npub vs full)
      let skipped = false;
      for (const s of skip) {
        if (s && (key.includes(s) || s.includes(key))) {
          skipped = true;
          break;
        }
      }
      if (skipped) continue;
      seen.add(key);
      refs.push(r);
      if (refs.length >= maxRefs) break;
    }
    if (refs.length >= maxRefs) break;
  }
  if (refs.length === 0) return null;

  const parts: string[] = [];
  for (const r of refs) {
    if (options?.signal?.aborted) break;
    const result = await fetchNostrEntity(r, query, { signal: options?.signal });
    if (result.ok) {
      parts.push(result.text);
    } else {
      parts.push(`### Nostr ref ${result.ref}\n(unavailable: ${result.error})`);
    }
  }
  if (parts.length === 0) return null;
  return (
    `# Fetched Nostr content\n` +
    `The following was resolved from Nostr references in the conversation or project context. ` +
    `Treat it as reference material the user may want discussed.\n\n` +
    parts.join('\n\n')
  );
}

// Silence unused if tree-shaken differently
void formatEvent;
void formatProfile;
